import type { Template } from '@/api/templates';
import { DispatchModeBadge, type DispatchMode } from '@/features/templates/dispatchMode';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChannelName,
  EmptyState,
  LoadingState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  getCategoryLabel,
  getScheduleTypeLabel,
  getTechnicalTypeLabel,
  getTemplateChannelId,
  getTemplateChannelName,
  isWabaBackedTemplate,
} from '@/features/templates/templateTaxonomy';
import { formatDateTime } from '@/utils/formatters';

type ScenarioBadge = {
  key: string;
  label: string;
  variant: 'secondary' | 'success' | 'warning' | 'info';
  title?: string;
};

interface TemplatesTableProps {
  isLoading: boolean;
  isSuperAdmin: boolean;
  selectedTenantId: string;
  templates: Template[];
  selectedIds: Set<string>;
  isBulkLoading: boolean;
  hasActiveFilters: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  getTenantName: (tenantId: string) => string;
  getScenarioBadges: (template: Template) => ScenarioBadge[];
  getDispatchModes: (template: Template) => DispatchMode[];
  getRelatedAutoReplies: (template: Template) => Array<{
    key: string;
    label: string;
    title: string;
    is_active: boolean;
  }>;
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onClearSelection: () => void;
  onCreate: () => void;
  onPreview: (id: string) => void;
  onEdit: (template: Template) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, nextValue: boolean) => void;
}

const stickyHead =
  'sticky top-0 z-20 bg-shell-elevated/95 backdrop-blur-sm shadow-[inset_0_-1px_0_var(--shell-border)]';
const stickyNameHead =
  'sticky left-0 z-30 bg-shell-elevated/95 backdrop-blur-sm shadow-[inset_0_-1px_0_var(--shell-border),1px_0_0_var(--shell-border)]';
const stickyNameCell =
  'sticky left-0 z-10 bg-shell-surface shadow-[1px_0_0_var(--shell-border)] group-hover:bg-shell-hover';

export function TemplatesTable({
  isLoading,
  isSuperAdmin,
  selectedTenantId,
  templates,
  selectedIds,
  isBulkLoading,
  hasActiveFilters,
  canRead,
  canUpdate,
  canDelete,
  getTenantName,
  getScenarioBadges,
  getDispatchModes,
  getRelatedAutoReplies,
  onSelectAll,
  onSelectOne,
  onBulkActivate,
  onBulkDeactivate,
  onClearSelection,
  onCreate,
  onPreview,
  onEdit,
  onDelete,
  onToggleActive,
}: TemplatesTableProps) {
  return (
    <Card className="border-shell-border bg-shell-surface shadow-none">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base font-extrabold tracking-tight text-shell-text">
            Список шаблонов
          </CardTitle>
          <p className="text-sm text-shell-muted">{templates.length} в текущей выборке</p>
        </div>
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-shell-border bg-shell-elevated/70 px-3 py-2">
            <span className="text-sm font-semibold text-shell-muted">Выбрано: {selectedIds.size}</span>
            <Button variant="secondary" size="sm" onClick={onBulkActivate} disabled={isBulkLoading}>
              Активировать
            </Button>
            <Button variant="secondary" size="sm" onClick={onBulkDeactivate} disabled={isBulkLoading}>
              Деактивировать
            </Button>
            <Button variant="ghost" size="sm" onClick={onClearSelection}>
              Сбросить
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <LoadingState />
        ) : isSuperAdmin && !selectedTenantId ? (
          <EmptyState
            type="search"
            title="Выберите клинику"
            description="Выберите клинику в фильтре выше для просмотра шаблонов"
          />
        ) : templates.length === 0 ? (
          <EmptyState
            type="search"
            title={hasActiveFilters ? 'Шаблоны не найдены' : 'Нет шаблонов'}
            description={
              hasActiveFilters ? 'Попробуйте изменить параметры поиска' : 'Создайте первый шаблон'
            }
            actionText="Создать шаблон"
            onAction={onCreate}
          />
        ) : (
          <div className="max-h-[min(70vh,52rem)] overflow-auto rounded-2xl border border-shell-border bg-shell-surface">
            <Table wrapped={false} className="min-w-[1100px]">
              <TableHead>
                <TableRow className="hover:bg-transparent">
                  <TableHeader className={`${stickyHead} w-10 px-3 py-2.5`}>
                    <input
                      type="checkbox"
                      checked={templates.length > 0 && selectedIds.size === templates.length}
                      onChange={(e) => onSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-shell-border text-shell-primary focus:ring-shell-primary/40"
                    />
                  </TableHeader>
                  {isSuperAdmin && (
                    <TableHeader className={`${stickyHead} px-3 py-2.5`}>Клиника</TableHeader>
                  )}
                  <TableHeader className={`${stickyNameHead} min-w-[14rem] px-3 py-2.5`}>
                    Название
                  </TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Тех. тип</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Тематика</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Канал</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Переменные</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Автоответы</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Статус</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Создан</TableHeader>
                  <TableHeader className={`${stickyHead} px-3 py-2.5`}>Действия</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => {
                  const scenarioBadges = getScenarioBadges(template);
                  const dispatchModes = getDispatchModes(template);
                  const related = getRelatedAutoReplies(template);
                  const variablesTitle = template.variables.join(', ');

                  return (
                    <TableRow
                      key={template.id}
                      testId={`row-template-${template.id}`}
                      className={`group ${canUpdate ? 'cursor-pointer' : ''}`}
                      onClick={
                        canUpdate
                          ? () => {
                              onEdit(template);
                            }
                          : undefined
                      }
                    >
                      <TableCell
                        className="px-3 py-2.5 whitespace-nowrap"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(template.id)}
                          onChange={(e) => onSelectOne(template.id, e.target.checked)}
                          className="h-4 w-4 rounded border-shell-border text-shell-primary focus:ring-shell-primary/40"
                        />
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="px-3 py-2.5 text-shell-muted">
                          {getTenantName(template.tenant_id)}
                        </TableCell>
                      )}
                      <TableCell className={`${stickyNameCell} min-w-[14rem] px-3 py-2.5 whitespace-normal`}>
                        <div className="space-y-1.5">
                          <div className="font-semibold text-shell-text">{template.name}</div>
                          {dispatchModes.length > 0 || scenarioBadges.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {dispatchModes.map((mode) => (
                                <DispatchModeBadge key={`${template.id}-${mode}`} mode={mode} />
                              ))}
                              {scenarioBadges.slice(0, 2).map((badge) => (
                                <Badge
                                  key={`${template.id}-${badge.key}`}
                                  variant={badge.variant}
                                  className="text-[11px]"
                                  title={badge.title}
                                >
                                  {badge.label}
                                </Badge>
                              ))}
                              {scenarioBadges.length > 2 ? (
                                <span
                                  className="text-[11px] text-shell-muted"
                                  title={scenarioBadges
                                    .slice(2)
                                    .map((badge) => badge.label)
                                    .join(', ')}
                                >
                                  +{scenarioBadges.length - 2}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        <Badge variant={isWabaBackedTemplate(template) ? 'info' : 'secondary'}>
                          {getTechnicalTypeLabel(template)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {template.category && template.category !== 'manual' ? (
                            <Badge variant="info">{getCategoryLabel(template.category)}</Badge>
                          ) : null}
                          {template.send_schedule?.type ? (
                            <Badge variant="secondary" className="text-[11px]">
                              {getScheduleTypeLabel(template.send_schedule.type)}
                            </Badge>
                          ) : null}
                          {!template.category &&
                            !template.send_schedule?.type &&
                            scenarioBadges.length === 0 && (
                              <span className="text-sm text-shell-muted">—</span>
                            )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        {(() => {
                          const channelId = getTemplateChannelId(template);
                          if (!channelId) {
                            return <span className="text-sm text-shell-muted">Любой</span>;
                          }
                          return (
                            <Badge variant="info" className="gap-1.5 pl-1.5">
                              <ChannelName
                                channelId={channelId}
                                label={getTemplateChannelName(template)}
                                size={14}
                              />
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        {template.variables.length === 0 ? (
                          <span className="text-sm text-shell-muted">—</span>
                        ) : (
                          <span
                            className="text-sm font-medium text-shell-text"
                            title={variablesTitle}
                          >
                            {template.variables.length} шт.
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-3 py-2.5">
                        {related.length === 0 ? (
                          <span className="text-sm text-shell-muted">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {related.slice(0, 2).map((rule) => (
                              <span key={rule.key} title={rule.title}>
                                <Badge
                                  variant={rule.is_active ? 'success' : 'secondary'}
                                  className="cursor-help text-[11px]"
                                >
                                  {rule.label}
                                </Badge>
                              </span>
                            ))}
                            {related.length > 2 ? (
                              <span className="text-[11px] text-shell-muted">
                                +{related.length - 2}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className="px-3 py-2.5"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => onToggleActive(template.id, !template.is_active)}
                          className="cursor-pointer"
                          data-testid={`btn-toggle-active-${template.id}`}
                          disabled={!canUpdate}
                        >
                          <StatusBadge status={template.is_active} />
                        </button>
                      </TableCell>
                      <TableCell className="px-3 py-2.5 text-shell-muted">
                        {formatDateTime(template.created_at)}
                      </TableCell>
                      <TableCell
                        className="px-3 py-2.5"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="flex flex-wrap items-center gap-1">
                          {canRead ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2.5 text-xs font-semibold"
                              onClick={() => onPreview(template.id)}
                              testId={`template-${template.id}-view`}
                            >
                              Открыть
                            </Button>
                          ) : null}
                          {canUpdate ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2.5 text-xs font-semibold"
                              onClick={() => onEdit(template)}
                              testId={`template-${template.id}-edit`}
                            >
                              Изменить
                            </Button>
                          ) : null}
                          {canDelete ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2.5 text-xs font-semibold text-shell-danger"
                              onClick={() => onDelete(template.id)}
                              testId={`template-${template.id}-delete`}
                            >
                              Удалить
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
